<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import MemberInfoHeader from '@/components/collectivites/MemberInfoHeader.vue';
import membersService from '@/services/membersService';
import { formatAmount, formatDateFrench } from '@/utils/formatters';

const route = useRoute();
const router = useRouter();

const memberId = computed(() => Number(route.params.id));
const member = ref<any>(null);
const items = ref<any[]>([]);
const stats = ref({ debloque: 0, debloque_percent: 0, bloque: 0, bloque_percent: 0, total: 0 });
const loading = ref(false);
const errorMessage = ref('');

function goBack() {
    router.push({ name: 'collectivites-membres' });
}

const monthsFr = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

function moisLabel(detail: any): string {
    if (detail.date_prevision) {
        const d = new Date(detail.date_prevision);
        if (!isNaN(d.getTime())) return `${d.getDate().toString().padStart(2, '0')} ${monthsFr[d.getMonth()]} ${d.getFullYear()}`;
    }
    if (typeof detail.mois === 'number') return `Mois ${detail.mois}`;
    return '-';
}

async function loadAll() {
    if (!memberId.value) return;
    loading.value = true;
    errorMessage.value = '';
    try {
        const [m, d] = await Promise.all([
            membersService.getMember(memberId.value),
            membersService.getMemberDecaissementsMensuels(memberId.value),
        ]);
        member.value = m;
        items.value = d?.items || [];
        stats.value = d?.stats || { debloque: 0, debloque_percent: 0, bloque: 0, bloque_percent: 0, total: 0 };
    } catch (e: any) {
        errorMessage.value = e?.message || 'Erreur lors du chargement.';
    } finally {
        loading.value = false;
    }
}

onMounted(loadAll);

const headerProgress = computed(() => {
    const pct = stats.value.debloque_percent ?? 0;
    return {
        label: 'Débloqué',
        percent: pct,
        valueLabel: `${Math.round(pct)}%`,
    };
});

function statutTag(statut: string) {
    if (statut === 'done') return { class: 'notif', label: 'Effectué' };
    if (statut === 'cancelled') return { class: 'danger', label: 'Annulé' };
    return { class: 'warning', label: 'En attente' };
}
</script>

<template>
    <section class="container-full-1 bg-grey-50">
        <div class="d-flex align-items-center gap-4 mb-6">
            <button class="btn white2" @click="goBack">
                <img src="/public/icons/icon-back.svg" class="btn-icon" alt="" />
                <span>Retour</span>
            </button>
            <h2 class="secondary-font gap-4 mt-2 mb-0">Membre · Revolving</h2>
        </div>

        <div v-if="loading" class="flex-center py-10"><div class="loader dark"></div></div>
        <div v-if="errorMessage" class="alert alert-danger mb-4">{{ errorMessage }}</div>

        <MemberInfoHeader
            :member="member"
            :end-date="member?.souscription?.date_fin"
            :progress="headerProgress"
        />

        <div class="clear-both h-20"></div>

        <!-- Décaissement mensuel -->
        <div class="widget edit-card">
            <h3 class="edit-card-title">
                <img src="/public/icons/icon-data.svg" class="size-base" alt="" />
                Décaissement mensuel
            </h3>

            <div class="stats-grid mb-6">
                <div>
                    <h5 class="stat-label"><span class="circle-medium bg-color-1000"></span>Débloqué</h5>
                    <h1 class="stat-value">{{ formatAmount(stats.debloque) }} F</h1>
                    <h5 class="text-grey-600 mb-4">{{ Math.round(stats.debloque_percent || 0) }}%</h5>
                    <div class="stat-progress">
                        <div class="stat-progress-fill bg-color-1000 text-white" :style="`width: ${Math.max(2, Math.min(100, stats.debloque_percent || 0))}%;`">
                            {{ Math.round(stats.debloque_percent || 0) }}%
                        </div>
                    </div>
                </div>
                <div>
                    <h5 class="stat-label"><span class="circle-medium bg-purple"></span>Bloqué</h5>
                    <h1 class="stat-value">{{ formatAmount(stats.bloque) }} F</h1>
                    <h5 class="text-grey-600 mb-4">{{ Math.round(stats.bloque_percent || 0) }}%</h5>
                    <div class="stat-progress">
                        <div class="stat-progress-fill bg-purple text-white" :style="`width: ${Math.max(2, Math.min(100, stats.bloque_percent || 0))}%;`">
                            {{ Math.round(stats.bloque_percent || 0) }}%
                        </div>
                    </div>
                </div>
                <div>
                    <h5 class="stat-label"><span class="circle-medium bg-green-light"></span>Total</h5>
                    <h1 class="stat-value">{{ formatAmount(stats.total) }} F</h1>
                    <h5 class="text-grey-600 mb-4">100%</h5>
                    <div class="stat-progress">
                        <div class="stat-progress-fill bg-green-light" style="width: 100%;">100%</div>
                    </div>
                </div>
            </div>

            <h3 class="edit-card-title mt-6">
                <img src="/public/icons/icon-data.svg" class="size-base" alt="" />
                Détail mensuel
            </h3>

            <div v-if="items.length === 0" class="text-center text-grey-500 py-5">
                Aucun décaissement prévu.
            </div>

            <div v-else class="decaissements-grid">
                <div v-for="detail in items" :key="detail.id" class="decaissement-card" :class="{ done: detail.statut === 'done' }">
                    <div>
                        <h6 class="font-semibold mb-1">{{ moisLabel(detail) }}</h6>
                        <h3 class="font-bold">{{ formatAmount(detail.montant) }} F</h3>
                    </div>
                    <div class="decaissement-meta">
                        <span class="tag-state stroke" :class="statutTag(detail.statut).class">
                            {{ statutTag(detail.statut).label }}
                        </span>
                        <p class="text-sm text-grey-500 mt-2 mb-0" v-if="detail.date_operation">
                            Le {{ formatDateFrench(detail.date_operation) }}
                        </p>
                        <p class="text-sm text-grey-500 mt-2 mb-0" v-else-if="detail.countdown">
                            {{ detail.countdown }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div class="clear-both h-50"></div>
    </section>
</template>

<style scoped>
.edit-card {
    padding: 1.75rem;
}

.edit-card-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: rgb(var(--primary-color-rgb), 1);
    font-weight: 600;
    margin: 0 0 1.25rem;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1.5rem;
}

.stat-label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: rgba(0, 0, 0, 0.6);
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.stat-value {
    font-size: 2rem;
    font-weight: 700;
    margin: 0.25rem 0 0.5rem;
}

.stat-progress {
    height: 36px;
    border-radius: 100px;
    background-color: rgba(0, 0, 0, 0.08);
    overflow: hidden;
}

.stat-progress-fill {
    height: 100%;
    border-radius: 100px;
    display: flex;
    align-items: center;
    padding: 0 1rem;
    font-weight: 600;
    transition: width 0.3s ease;
}

.decaissements-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1rem;
}

.decaissement-card {
    border: 2px solid rgba(0, 0, 0, 0.08);
    border-radius: 20px;
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    background: white;
    transition: all 0.2s ease;
}

.decaissement-card.done {
    border-color: rgba(var(--color-success-rgb), 0.4);
    background-color: rgba(var(--color-success-rgb), 0.04);
}

.decaissement-card h3 {
    margin: 0;
}

.decaissement-meta {
    text-align: right;
}

.decaissement-meta p {
    margin: 0;
}

.circle-medium {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
}
</style>
